import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth, formatApiError } from "./AuthContext";
import { setSiteContentCache } from "../hooks/useSiteContent";
import { UploadCloud } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  { section: "Header & branding", items: [
    { key: "logo_image", label: "Logo image", type: "image" },
    { key: "brand_tagline", label: "Tagline beside logo", type: "text" },
  ]},
  { section: "Hero", items: [
    { key: "hero_overline", label: "Overline", type: "text" },
    { key: "hero_headline_lead", label: "Headline · first part", type: "text" },
    { key: "hero_headline_emphasis", label: "Headline · italic emphasis", type: "text" },
    { key: "hero_headline_trail", label: "Headline · last part", type: "text" },
    { key: "hero_subtext", label: "Subtext", type: "textarea" },
    { key: "hero_cta", label: "Primary button label", type: "text" },
    { key: "hero_cta_secondary", label: "Secondary link label", type: "text" },
    { key: "hero_image", label: "Hero photo", type: "image" },
    { key: "hero_quote_overline", label: "Pull-quote overline", type: "text" },
    { key: "hero_quote", label: "Pull quote", type: "text" },
  ]},
  { section: "Three quiet vantage points", items: [
    { key: "audience_overline", label: "Overline", type: "text" },
    { key: "audience_heading", label: "Section heading", type: "text" },
    { key: "audience_1_label", label: "Card 1 · label", type: "text" },
    { key: "audience_1_title", label: "Card 1 · title", type: "text" },
    { key: "audience_1_body", label: "Card 1 · body", type: "textarea" },
    { key: "audience_1_image", label: "Card 1 · photo", type: "image" },
    { key: "audience_2_label", label: "Card 2 · label", type: "text" },
    { key: "audience_2_title", label: "Card 2 · title", type: "text" },
    { key: "audience_2_body", label: "Card 2 · body", type: "textarea" },
    { key: "audience_2_image", label: "Card 2 · photo", type: "image" },
    { key: "audience_3_label", label: "Card 3 · label", type: "text" },
    { key: "audience_3_title", label: "Card 3 · title", type: "text" },
    { key: "audience_3_body", label: "Card 3 · body", type: "textarea" },
    { key: "audience_3_image", label: "Card 3 · photo", type: "image" },
  ]},
  { section: "Signature philosophy", items: [
    { key: "signature_overline", label: "Overline", type: "text" },
    { key: "signature_marquee", label: "Marquee phrases (separate with · )", type: "text" },
    { key: "signature_text", label: "Main paragraph", type: "textarea" },
    { key: "signature_body", label: "Body note", type: "textarea" },
    { key: "signature_image", label: "Signature photo", type: "image" },
  ]},
  { section: "Journal section", items: [
    { key: "journal_overline", label: "Overline", type: "text" },
    { key: "journal_heading", label: "Heading", type: "text" },
    { key: "journal_description", label: "Description", type: "textarea" },
  ]},
  { section: "About", items: [
    { key: "about_overline", label: "Overline", type: "text" },
    { key: "about_heading", label: "Heading", type: "text" },
    { key: "about_body_1", label: "Paragraph one", type: "textarea" },
    { key: "about_body_2", label: "Paragraph two", type: "textarea" },
    { key: "about_body_3", label: "Italic closing line", type: "textarea" },
    { key: "about_image", label: "About photo", type: "image" },
  ]},
  { section: "Email signup", items: [
    { key: "signup_overline", label: "Overline", type: "text" },
    { key: "signup_heading", label: "Heading", type: "text" },
    { key: "signup_description", label: "Description", type: "textarea" },
    { key: "signup_button", label: "Button label", type: "text" },
    { key: "signup_success", label: "Success message", type: "text" },
  ]},
  { section: "Footer", items: [
    { key: "footer_image", label: "Footer photo (top banner)", type: "image" },
    { key: "footer_tagline", label: "Tagline paragraph", type: "textarea" },
    { key: "footer_italic", label: "Italic closing line", type: "text" },
    { key: "footer_domain", label: "Domain label", type: "text" },
    { key: "footer_instagram_handle", label: "Instagram handle", type: "text" },
    { key: "footer_instagram_url", label: "Instagram URL", type: "text" },
    { key: "footer_email", label: "Contact email", type: "text" },
  ]},
];

function ImageField({ value, onChange, authHeaders, setError, testid }) {
  const [uploading, setUploading] = useState(false);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/media`, fd, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      // Save as a relative path so the same record works in preview & on
      // any production domain — the browser resolves it against the current origin.
      onChange(data.url);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mt-2 space-y-3">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <input
          data-testid={testid}
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://… or /api/media/… or upload below"
          className="quiet-input flex-1"
        />
        <label className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-border text-sm text-foreground/80 hover:bg-secondary cursor-pointer flex-shrink-0">
          <UploadCloud size={16} strokeWidth={1.5} />
          {uploading ? "Uploading…" : "Upload"}
          <input
            data-testid={`${testid}-upload`}
            type="file"
            accept="image/*"
            onChange={onUpload}
            className="hidden"
          />
        </label>
      </div>
      {value && (
        <img
          src={value}
          alt=""
          className="w-full max-w-xs h-40 object-cover rounded-2xl border border-border/40"
        />
      )}
    </div>
  );
}

export default function AdminContent() {
  const { API, authHeaders } = useAuth();
  const [content, setContent] = useState(null);
  const [activeSection, setActiveSection] = useState(FIELDS[0].section);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/site/content`)
      .then(({ data }) => setContent(data))
      .catch((e) => setError(formatApiError(e)));
  }, [API]);

  const update = (k, v) => setContent((c) => ({ ...c, [k]: v }));

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.put(`${API}/admin/site/content`, content, {
        headers: authHeaders,
      });
      setContent(data);
      setSiteContentCache(data);
      setSavedAt(new Date());
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!content) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const active = FIELDS.find((s) => s.section === activeSection) || FIELDS[0];

  return (
    <div data-testid="admin-content">
      <p className="text-xs uppercase tracking-[0.3em] text-primary">Homepage</p>
      <h1 className="mt-3 font-serif text-4xl sm:text-5xl font-light">Site content</h1>
      <p className="mt-4 text-base text-muted-foreground max-w-xl">
        Every photo and every word on the public site lives here. Pick a section
        on the left, edit, then save. Changes are live immediately.
      </p>

      <div className="mt-12 grid lg:grid-cols-[220px,1fr] gap-10">
        {/* Section navigator */}
        <nav data-testid="content-section-nav" className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible lg:sticky lg:top-6 self-start">
          {FIELDS.map((s) => (
            <button
              key={s.section}
              type="button"
              data-testid={`section-${s.section.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setActiveSection(s.section)}
              className={`text-left px-4 py-3 rounded-2xl text-sm transition-colors whitespace-nowrap lg:whitespace-normal ${
                activeSection === s.section
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground/70 hover:bg-secondary"
              }`}
            >
              {s.section}
            </button>
          ))}
        </nav>

        {/* Active section editor */}
        <form onSubmit={onSave} className="space-y-10 min-w-0">
          <section>
            <h2 className="font-serif text-3xl font-light mb-2">{active.section}</h2>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-8">
              {active.items.length} fields
            </p>

            <div className="space-y-8">
              {active.items.map((f) => (
                <div key={f.key}>
                  <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    {f.label}
                  </label>
                  {f.type === "textarea" ? (
                    <textarea
                      data-testid={`content-${f.key}`}
                      value={content[f.key] || ""}
                      onChange={(e) => update(f.key, e.target.value)}
                      rows={3}
                      className="w-full mt-2 bg-transparent border border-border rounded-2xl p-4 text-base leading-relaxed font-light focus:outline-none focus:border-primary"
                    />
                  ) : f.type === "image" ? (
                    <ImageField
                      value={content[f.key]}
                      onChange={(v) => update(f.key, v)}
                      authHeaders={authHeaders}
                      setError={setError}
                      testid={`content-${f.key}`}
                    />
                  ) : (
                    <input
                      data-testid={`content-${f.key}`}
                      value={content[f.key] || ""}
                      onChange={(e) => update(f.key, e.target.value)}
                      className="quiet-input mt-2"
                    />
                  )}
                </div>
              ))}
            </div>
          </section>

          {error && <p className="text-sm italic font-serif text-destructive">{error}</p>}

          <div className="flex items-center gap-6 sticky bottom-6 bg-background/90 backdrop-blur-md border border-border/60 rounded-full px-6 py-3 w-fit">
            <button
              data-testid="save-content"
              type="submit"
              disabled={saving}
              className="px-7 py-3 rounded-full bg-primary text-primary-foreground text-sm tracking-wide hover:bg-primary/90 disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
            {savedAt && (
              <span className="text-xs italic font-serif text-primary">
                Saved {savedAt.toLocaleTimeString()}
              </span>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
